import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth-store';
import { initSyncListeners } from '@/offline/sync';
import { initStorageQuota } from '@/offline/storage-quota';
import { loadExtractedProtocols } from '@/lib/extracted-protocols';
import { resumePendingTransmissions } from '@/lib/transmission-service';
import { useThemeStore } from '@/lib/theme-store';
import { useUILayoutStore } from '@/lib/ui-layout-store';
import { AppShell } from '@/components/tablet/AppShell';
import { LoginPage } from '@/components/LoginPage';
import { ResetPasswordPage } from '@/components/ResetPasswordPage';
import { BuildInfoBadge } from '@/components/BuildInfoBadge';
import { DevicePreview } from '@/components/DevicePreview';
import { InstallPrompt } from '@/components/InstallPrompt';
import { KeyboardController } from '@/components/KeyboardController';
import { DemoModeBanner } from '@/components/DemoModeBanner';
import { IdleWarningToast } from '@/components/IdleWarningToast';
import { useDemoMode } from '@/lib/demo-mode-store';
import { preloadAfterSignIn } from '@/lib/preload-service';
import { startIdleTimer } from '@/lib/idle-timer';
import { ensureDeviceAttributes } from '@/lib/use-device';
import { startBuildVersionPoller } from '@/lib/build-version-poller';
import { NewBuildToast } from '@/components/NewBuildToast';
// Eager — hot-path surfaces that must render on first paint without
// a Suspense gap. Dashboard is the landing surface after sign-in;
// EPCRPage is the highest-stakes interactive surface.
import { DashboardPage } from '@/modules/incident/DashboardPage';
import { EPCRPage } from '@/modules/epcr/EPCRPage';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Lazy — everything else. Vite emits a separate chunk per `import()`,
// dropping initial JS payload by >50% (Sprint-4 Finding 70). The
// `then(m => ({ default: m.X }))` adapter is required because our
// modules use named exports, not defaults.
const NewIncidentPage             = lazy(() => import('@/modules/incident/NewIncidentPage').then(m => ({ default: m.NewIncidentPage })));
const ProtocolsPage               = lazy(() => import('@/modules/protocols/ProtocolsPage').then(m => ({ default: m.ProtocolsPage })));
const TelehealthPage              = lazy(() => import('@/modules/telehealth/TelehealthPage').then(m => ({ default: m.TelehealthPage })));
const IncidentDetailPage          = lazy(() => import('@/modules/incident/IncidentDetailPage').then(m => ({ default: m.IncidentDetailPage })));
const ReportsPage                 = lazy(() => import('@/modules/reports/ReportsPage').then(m => ({ default: m.ReportsPage })));
const QAReviewPage                = lazy(() => import('@/modules/admin/QAReviewPage').then(m => ({ default: m.QAReviewPage })));
const FirefighterRosterPage       = lazy(() => import('@/modules/admin/FirefighterRosterPage').then(m => ({ default: m.FirefighterRosterPage })));
const EAPPage                     = lazy(() => import('@/modules/admin/EAPPage').then(m => ({ default: m.EAPPage })));
const SeriousIncidentAdminPage    = lazy(() => import('@/modules/admin/SeriousIncidentAdminPage').then(m => ({ default: m.SeriousIncidentAdminPage })));
const AdminConsolePage            = lazy(() => import('@/modules/admin/AdminConsolePage').then(m => ({ default: m.AdminConsolePage })));
const ProtocolMappingPage         = lazy(() => import('@/modules/admin/ProtocolMappingPage').then(m => ({ default: m.ProtocolMappingPage })));
const OutcomeFeedbackPage         = lazy(() => import('@/modules/admin/OutcomeFeedbackPage').then(m => ({ default: m.OutcomeFeedbackPage })));
const RequiredFieldsAlgorithmPage = lazy(() => import('@/modules/admin/RequiredFieldsAlgorithmPage').then(m => ({ default: m.RequiredFieldsAlgorithmPage })));
const DataPipelinePage            = lazy(() => import('@/modules/admin/DataPipelinePage').then(m => ({ default: m.DataPipelinePage })));
const PreviewLogPage              = lazy(() => import('@/modules/admin/PreviewLogPage').then(m => ({ default: m.PreviewLogPage })));
const EpcrAccessLogPage           = lazy(() => import('@/modules/admin/EpcrAccessLogPage').then(m => ({ default: m.EpcrAccessLogPage })));
const SignInLogPage               = lazy(() => import('@/modules/admin/SignInLogPage').then(m => ({ default: m.SignInLogPage })));
const PatientsAdminPage           = lazy(() => import('@/modules/admin/PatientsAdminPage').then(m => ({ default: m.PatientsAdminPage })));
const ComstarConfigPage           = lazy(() => import('@/modules/admin/ComstarConfigPage').then(m => ({ default: m.ComstarConfigPage })));
const RiemsisConfigPage           = lazy(() => import('@/modules/admin/RiemsisConfigPage').then(m => ({ default: m.RiemsisConfigPage })));
const QAMismatchPage              = lazy(() => import('@/modules/admin/QAMismatchPage').then(m => ({ default: m.QAMismatchPage })));
const PipelineLogicPage           = lazy(() => import('@/modules/preview/PipelineLogicPage').then(m => ({ default: m.PipelineLogicPage })));
const FireReportPage              = lazy(() => import('@/modules/fire/FireReportPage').then(m => ({ default: m.FireReportPage })));
const DebugPage                   = lazy(() => import('@/modules/debug/DebugPage').then(m => ({ default: m.DebugPage })));
const ApparatusInventoryPage            = lazy(() => import('@/modules/inventory/ApparatusInventoryPage').then(m => ({ default: m.ApparatusInventoryPage })));
const PrintableTOC                = lazy(() => import('@/modules/epcr/PrintableTOC').then(m => ({ default: m.PrintableTOC })));
const PrintableAuditReport        = lazy(() => import('@/modules/epcr/PrintableAuditReport').then(m => ({ default: m.PrintableAuditReport })));
const BillingPortalMockup         = lazy(() => import('@/modules/design-preview/BillingPortalMockup').then(m => ({ default: m.BillingPortalMockup })));
import { SyncStatusBanner } from '@/components/SyncStatusBanner';

/**
 * Suspense fallback for lazy routes. Intentionally minimal — most
 * route chunks are tiny and resolve sub-100ms; a heavy loading
 * spinner would just flash. The wordmark pulse here matches the
 * pre-auth loading screen for visual continuity.
 */
function RouteSpinner() {
  return (
    <div className="flex min-h-[40dvh] items-center justify-center" aria-live="polite" aria-busy="true">
      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
        Loading…
      </span>
    </div>
  );
}

export function App() {
  const { user, loading, initialize } = useAuthStore();

  useEffect(() => {
    // Combined Addendum §7 — three-layer device detection. Runs first so
    // platform-specific CSS selectors light up before theme/layout init.
    ensureDeviceAttributes();
    useThemeStore.getState().init();
    useUILayoutStore.getState().init();
    useDemoMode.getState().init();
    initialize();
    initSyncListeners();
    // Request IndexedDB persistence + start the 60s usage poll. Finding 22.
    void initStorageQuota();
    // Kick off the lazy load of the 1.9 MB extracted-protocols catalog
    // chunk so it's available by the time anyone opens an ePCR. The
    // 9 critical hand-built protocols are already synchronous in
    // care-matrix.ts, so the runtime has coverage during this load.
    // Finding 75.
    void loadExtractedProtocols();
    resumePendingTransmissions();
    // HIPAA §164.312(a)(2)(iii) automatic logoff. Idempotent — only
    // wires listeners on first call; safe under React strict-mode.
    startIdleTimer();
    // Poll /build-info.json for new deploys; emits 'build:updated' that
    // the NewBuildToast (mounted below) listens for and prompts reload.
    startBuildVersionPoller();
  }, [initialize]);

  // Warm the offline cache as soon as we detect a signed-in user.
  // Runs at most once per 6h, non-blocking.
  useEffect(() => {
    if (!user) return;
    void preloadAfterSignIn();
  }, [user?.id]);

  if (loading) {
    // Load screen wordmark — mirrors LoginPage exactly: EMS bold + PCR
    // normal + "." in brand color with the 60-bpm heartbeat pulse.
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-slate-950">
        <div className="text-center">
          <h1 className="text-5xl tracking-tight text-slate-100 leading-none">
            <span className="font-black">EMS</span>
            <span className="font-normal">PCR</span>
            <span className="wordmark-pulse text-brand-400 font-bold">.</span>
          </h1>
          <p className="mt-3 text-[10px] font-mono uppercase tracking-widest text-slate-500">
            Records Management System
          </p>
          <p className="mt-2 text-xs text-slate-500">Loading…</p>
        </div>
        <BuildInfoBadge />
      </div>
    );
  }

  // Allow /debug without login so deploy triage never blocks on auth
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const isDebug = path === '/debug';
  // Design-preview routes render their own chrome and MUST bypass
  // AppShell so the imported sidebar/topbar aren't wrapped in the
  // live dark-slate chrome. These are unlinked previews — only
  // reachable by typing the URL.
  const isDesignPreview = path.startsWith('/design-preview');
  // Hidden build-logic preview — unlinked URL for chiefs / outside
  // reviewers. NDA-gated; logged in pipeline_preview_visits. Bypasses
  // the auth wall because the audience here is intentionally
  // anonymous (they don't have an EMSPCR account yet).
  const isPipelinePreview = path.startsWith('/preview');
  // Public daily apparatus inventory sheet. Crews open this on a phone at shift
  // change without an account, so it bypasses the auth wall the same way
  // /preview does. Matched case-insensitively: the link is published as
  // /Rescue1 but people type /rescue1.
  const isApparatusInventory = /^\/rescue1\/?$/i.test(path);
  // Password recovery lands here from the email link — must be reachable
  // without a full login (the recovery token puts us in a limited session).
  // We also detect the recovery token in the URL fragment in case Supabase
  // dropped us on `/` (which it does if the redirect URL isn't whitelisted
  // in the project's Auth → URL Configuration allow-list). This makes the
  // reset flow work regardless of which path the email link lands on.
  const hasRecoveryHash = hash.includes('type=recovery') || hash.includes('access_token=');
  const hasRecoveryCode = search.includes('code=') && (search.includes('type=recovery') || path === '/reset-password');
  const isReset = path === '/reset-password' || hasRecoveryHash || hasRecoveryCode;

  if (isReset) {
    return (
      <>
        <ResetPasswordPage />
        <BuildInfoBadge />
      </>
    );
  }

  // Apparatus inventory renders for everyone, signed in or not, and outside
  // AppShell so a crew on a phone gets the whole screen for the checklist.
  if (isApparatusInventory) {
    return (
      <>
        <Suspense fallback={<RouteSpinner />}>
          <ApparatusInventoryPage />
        </Suspense>
      </>
    );
  }

  if (!user && !isDebug && !isPipelinePreview && !isDesignPreview && !isApparatusInventory) {
    // Anonymous catchment: straight to LoginPage. The pre-auth
    // pitch landing was removed per user direction — sign-in is the
    // entry surface now. Any deep link bounces here too.
    return (
      <>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<LoginPage />} />
        </Routes>
        <BuildInfoBadge />
      </>
    );
  }

  // Pipeline-preview route — anonymous, NDA-gated, traffic-logged.
  // Lives outside AppShell so chiefs see only the focused preview
  // without our internal navigation chrome.
  if (isPipelinePreview) {
    return (
      <>
        <Suspense fallback={<RouteSpinner />}>
          <Routes>
            <Route path="/preview/build-logic" element={<PipelineLogicPage />} />
            <Route path="*" element={<Navigate to="/preview/build-logic" replace />} />
          </Routes>
        </Suspense>
        <BuildInfoBadge />
      </>
    );
  }

  // Debug route available without shell chrome for clean triage
  if (isDebug) {
    return (
      <div className="min-h-[100dvh] bg-slate-950 p-4 sm:p-6">
        <Suspense fallback={<RouteSpinner />}>
          <DebugPage />
        </Suspense>
        <BuildInfoBadge />
      </div>
    );
  }

  // Design preview routes — bypass AppShell to render imported
  // design systems cleanly. Unlinked from main nav.
  if (isDesignPreview) {
    return (
      <>
        <Suspense fallback={<RouteSpinner />}>
          <Routes>
            <Route path="/design-preview/billing-portal" element={<BillingPortalMockup />} />
            <Route path="*" element={<Navigate to="/design-preview/billing-portal" replace />} />
          </Routes>
        </Suspense>
        <BuildInfoBadge />
      </>
    );
  }

  return (
    <ErrorBoundary label="EMSPCR app">
    <DevicePreview>
      <DemoModeBanner />
      <SyncStatusBanner />
      <AppShell>
        <Suspense fallback={<RouteSpinner />}>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          {/* /incidents/new is a legacy URL. Create flow no longer
              has a form — Dashboard's "Create New Incident" button
              materializes a blank incident inline and routes
              straight to the detail page. Anyone landing on /new
              (back button, bookmark, link) gets bounced home. */}
          <Route path="/incidents/new" element={<NewIncidentPage />} />
          <Route path="/protocols" element={<ProtocolsPage />} />
          <Route path="/telehealth" element={<TelehealthPage />} />
          <Route path="/incidents/:id" element={<IncidentDetailPage />} />
          {/* ErrorBoundary tightens around the ePCR — Finding 60. A crash
              inside a tab (Code99Mode, Vitals, etc.) now drops back to a
              recovery panel WITHOUT taking down the AppShell + workflow
              rail. Dexie data is unaffected by the unmount. */}
          <Route path="/incidents/:incidentId/epcr" element={
            <ErrorBoundary label="ePCR"><EPCRPage /></ErrorBoundary>
          } />
          <Route path="/incidents/:incidentId/epcr/:epcrId" element={
            <ErrorBoundary label="ePCR"><EPCRPage /></ErrorBoundary>
          } />
          <Route path="/incidents/:incidentId/epcr/:epcrId/print" element={<PrintableTOC />} />
          <Route path="/incidents/:incidentId/epcr/:epcrId/audit" element={<PrintableAuditReport />} />
          <Route path="/incidents/:incidentId/fire" element={<FireReportPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/qa" element={<QAReviewPage />} />
          <Route path="/admin/roster" element={<FirefighterRosterPage />} />
          <Route path="/admin/eap" element={<EAPPage />} />
          <Route path="/eap" element={<EAPPage />} />
          <Route path="/admin/serious-incidents" element={<SeriousIncidentAdminPage />} />
          <Route path="/admin/protocol-mapping" element={<ProtocolMappingPage />} />
          <Route path="/admin/outcome-feedback" element={<OutcomeFeedbackPage />} />
          <Route path="/admin/required-fields" element={<RequiredFieldsAlgorithmPage />} />
          <Route path="/admin/data-pipeline" element={<DataPipelinePage />} />
          <Route path="/admin/preview-log" element={<PreviewLogPage />} />
          <Route path="/admin/epcr-access-log" element={<EpcrAccessLogPage />} />
          <Route path="/admin/sign-in-log" element={<SignInLogPage />} />
          <Route path="/admin/patients" element={<PatientsAdminPage />} />
          <Route path="/admin/comstar-config" element={<ComstarConfigPage />} />
          <Route path="/admin/riemsis-config" element={<RiemsisConfigPage />} />
          <Route path="/admin/qa-mismatch" element={<QAMismatchPage />} />
          <Route path="/admin" element={<AdminConsolePage />} />
          <Route path="/debug" element={<DebugPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </AppShell>
      <InstallPrompt />
      <KeyboardController />
      <IdleWarningToast />
      <NewBuildToast />
      <BuildInfoBadge />
    </DevicePreview>
    </ErrorBoundary>
  );
}
